import { Button, useMantineTheme } from '@mantine/core';
import { Handle, Position, type NodeProps, useStore } from '@xyflow/react';
import { match } from 'ts-pattern';
import { CENTER, DOT_RADIUS, SIZE } from './config';
import { TerminalNodeState } from '../../types';
import { useGraphContext } from '../../context';
import { DrawerKind, NodePickerContextType } from '../../store/ui';
import { RFGraphNode } from '../../organisms/graph-view/utils';
import {
  TerminalHandleStyle,
  TerminalNodeCenter,
  TerminalNodeRoot,
  TerminalNodeWrapper,
} from './styles';

export interface TerminalNodeData {
  state?: TerminalNodeState;
  onStart?: () => void;
}

export const TerminalNode = ({
  state = TerminalNodeState.Idle,
}: TerminalNodeData) => {
  const theme = useMantineTheme();
  const strokeColor = theme.colors.gray[4];
  const ringColor = match(state)
    .with(TerminalNodeState.Success, () => theme.colors.green[6])
    .with(TerminalNodeState.Error, () => theme.colors.red[6])
    .otherwise(() => strokeColor);
  const centerSize = DOT_RADIUS * 2;

  return (
    <TerminalNodeRoot size={SIZE}>
      <TerminalNodeCenter size={centerSize} color={ringColor} />
      <Handle
        id="child"
        type="target"
        position={Position.Left}
        style={TerminalHandleStyle(centerSize)}
      />
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle
          cx={CENTER}
          cy={CENTER}
          r={7}
          fill="none"
          stroke={ringColor}
          strokeWidth={2}
          strokeDasharray="24 10"
          strokeDashoffset={8}
          strokeLinecap="round"
          transform={`rotate(10 ${CENTER} ${CENTER})`}
        />
      </svg>
    </TerminalNodeRoot>
  );
};

export const TerminalGraphNodeFlow = (props: NodeProps<RFGraphNode>) => {
  const [openDrawer, setFocusNodeId] = useGraphContext(({ ui }) => [
    ui.openDrawer,
    ui.setFocusNodeId,
  ]);
  const edges = useStore((s) => s.edges);
  const hasIncomingEdge = edges.some((edge) => edge.target === props.id);

  const handleStart = () => {
    setFocusNodeId(props.id);
    openDrawer({
      kind: DrawerKind.NodePicker,
      context: {
        mode: NodePickerContextType.AddChildToNode,
        nodeId: props.id,
        portId: 'child',
      },
    });
  };

  return (
    <TerminalNodeWrapper>
      {hasIncomingEdge ? (
        <TerminalNode />
      ) : (
        <Button size="xs" variant="default" onClick={handleStart}>
          Start Graph
        </Button>
      )}
    </TerminalNodeWrapper>
  );
};
